import {engineContext} from './context.js';

(function(global){
'use strict';

const VERSION='0.1.0-alpha';

const ALLOWED_STAGE_STATES={
  BUSINESS:['OPEN','REVIEW','BLOCKED','UNKNOWN'],
  STOCK:['ATTRACTIVE_ZONE','REVIEW','BLOCKED','UNKNOWN'],
  TIMING:['OPEN','REVIEW','RISK_ELEVATED','BLOCKED','UNKNOWN'],
  WHY_WRONG:['CLEAR','REVIEW','BLOCKED','UNKNOWN']
};

function normalizeStage(stage,value){
  const allowed=ALLOWED_STAGE_STATES[stage]||[];
  return allowed.includes(value)?value:'UNKNOWN';
}

function normalizeInput(x){
  x=x||{};
  return Object.freeze({
    symbol:x.symbol||null,
    scope:['OWNED','WATCHLIST','RESEARCH'].includes(x.scope)?x.scope:'RESEARCH',
    business:normalizeStage('BUSINESS',x.business),
    stock:normalizeStage('STOCK',x.stock),
    timing:normalizeStage('TIMING',x.timing),
    whyWrong:normalizeStage('WHY_WRONG',x.whyWrong),
    evidenceGapState:['READY_FOR_REVIEW','REVIEW_WITH_GAPS','BLOCKED_BY_EVIDENCE_GAP'].includes(x.evidenceGapState)
      ?x.evidenceGapState:'REVIEW_WITH_GAPS',
    entryGate:['OPEN','REVIEW','BLOCKED','UNKNOWN'].includes(x.entryGate)?x.entryGate:'UNKNOWN',
    verification:['VERIFIED','PARTIAL','UNVERIFIED'].includes(x.verification)?x.verification:'UNVERIFIED',
    contradictionCount:Number.isFinite(x.contradictionCount)?Math.max(0,Math.floor(x.contradictionCount)):0,
    unresolvedCriticalCount:Number.isFinite(x.unresolvedCriticalCount)?Math.max(0,Math.floor(x.unresolvedCriticalCount)):0
  });
}

function blockers(input){
  const x=normalizeInput(input);
  const out=[];

  if(x.business==='BLOCKED') out.push('BUSINESS_BLOCKED');
  if(x.stock==='BLOCKED') out.push('STOCK_BLOCKED');
  if(x.timing==='BLOCKED') out.push('TIMING_BLOCKED');
  if(x.whyWrong==='BLOCKED') out.push('WHY_WRONG_BLOCKED');
  if(x.evidenceGapState==='BLOCKED_BY_EVIDENCE_GAP') out.push('CRITICAL_EVIDENCE_GAP');
  if(x.entryGate==='BLOCKED') out.push('ENTRY_GATE_BLOCKED');
  if(x.unresolvedCriticalCount>0) out.push('UNRESOLVED_CRITICAL_ITEM');

  return Object.freeze(out);
}

function reviews(input){
  const x=normalizeInput(input);
  const out=[];

  if(x.business==='REVIEW'||x.business==='UNKNOWN') out.push('BUSINESS_REVIEW');
  if(x.stock==='REVIEW'||x.stock==='UNKNOWN') out.push('STOCK_REVIEW');
  if(x.timing==='REVIEW'||x.timing==='RISK_ELEVATED'||x.timing==='UNKNOWN') out.push('TIMING_REVIEW');
  if(x.whyWrong==='REVIEW'||x.whyWrong==='UNKNOWN') out.push('WHY_WRONG_REVIEW');
  if(x.evidenceGapState==='REVIEW_WITH_GAPS') out.push('EVIDENCE_GAP_REVIEW');
  if(x.entryGate==='REVIEW'||x.entryGate==='UNKNOWN') out.push('ENTRY_GATE_REVIEW');
  if(x.verification!=='VERIFIED') out.push('VERIFICATION_REVIEW');
  if(x.contradictionCount>0) out.push('CONTRADICTION_REVIEW');

  return Object.freeze(out);
}

function stageCompleteness(input){
  const x=normalizeInput(input);
  return Object.freeze({
    BUSINESS:x.business==='OPEN',
    STOCK:x.stock==='ATTRACTIVE_ZONE',
    TIMING:x.timing==='OPEN',
    WHY_WRONG:x.whyWrong==='CLEAR',
    EVIDENCE:x.evidenceGapState==='READY_FOR_REVIEW',
    ENTRY_GATE:x.entryGate==='OPEN'
  });
}

function readiness(input){
  const x=normalizeInput(input);
  const blocked=blockers(x);
  const review=reviews(x);
  const complete=stageCompleteness(x);

  let state='READY_FOR_DECISION';
  if(blocked.length) state='BLOCKED';
  else if(review.length) state='REVIEW';
  else if(x.verification!=='VERIFIED') state='REVIEW';

  return Object.freeze({
    symbol:x.symbol,
    scope:x.scope,
    state,
    blockers:blocked,
    reviews:review,
    stageCompleteness:complete,
    capitalPermission:false,
    tradeSignal:false,
    buySignal:false,
    sellSignal:false,
    entryGateOverride:false,
    companyQualityOverride:false
  });
}

function nextUnresolvedStage(input){
  const x=normalizeInput(input);

  if(x.business!=='OPEN') return 'BUSINESS';
  if(x.stock!=='ATTRACTIVE_ZONE') return 'STOCK';
  if(x.timing!=='OPEN') return 'TIMING';
  if(x.whyWrong!=='CLEAR') return 'WHY_WRONG';
  if(x.evidenceGapState!=='READY_FOR_REVIEW') return 'EVIDENCE';
  if(x.entryGate!=='OPEN') return 'ENTRY_GATE';
  return 'NONE';
}

function checkpoint(input){
  const r=readiness(input);
  const next=nextUnresolvedStage(input);

  return Object.freeze({
    symbol:r.symbol,
    state:r.state,
    next,
    headline:
      r.state==='BLOCKED'
        ?`Decision path blocked${next!=='NONE'?` at ${next}`:''}.`
        :r.state==='REVIEW'
          ?`Decision path needs review${next!=='NONE'?` at ${next}`:''}.`
          :`Research path is complete enough for human decision.`,
    blockers:r.blockers,
    reviews:r.reviews,
    capitalPermission:false,
    tradeSignal:false
  });
}

function batch(items){
  const rows=Array.isArray(items)?items:[];
  const results=rows.map(readiness);

  return Object.freeze({
    blocked:Object.freeze(results.filter(x=>x.state==='BLOCKED')),
    review:Object.freeze(results.filter(x=>x.state==='REVIEW')),
    ready:Object.freeze(results.filter(x=>x.state==='READY_FOR_DECISION')),
    total:results.length,
    tradeSignal:false
  });
}

function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});

  const ready={
    symbol:'AAA',scope:'WATCHLIST',
    business:'OPEN',
    stock:'ATTRACTIVE_ZONE',
    timing:'OPEN',
    whyWrong:'CLEAR',
    evidenceGapState:'READY_FOR_REVIEW',
    entryGate:'OPEN',
    verification:'VERIFIED',
    contradictionCount:0,
    unresolvedCriticalCount:0
  };

  let r=readiness(ready);
  ok('fully resolved path becomes ready for decision',r.state==='READY_FOR_DECISION');
  ok('ready does not mean capital permission',r.capitalPermission===false);
  ok('ready emits no trade signal',r.tradeSignal===false);
  ok('ready emits no buy signal',r.buySignal===false);
  ok('ready emits no sell signal',r.sellSignal===false);
  ok('ready never bypasses entry gate',r.entryGateOverride===false);
  ok('ready never overrides company quality',r.companyQualityOverride===false);

  r=readiness({...ready,business:'BLOCKED'});
  ok('business block blocks readiness',r.state==='BLOCKED');
  ok('business blocker is explicit',r.blockers.includes('BUSINESS_BLOCKED'));

  r=readiness({...ready,evidenceGapState:'BLOCKED_BY_EVIDENCE_GAP'});
  ok('critical evidence gap blocks readiness',r.state==='BLOCKED');
  ok('critical evidence blocker is explicit',r.blockers.includes('CRITICAL_EVIDENCE_GAP'));

  r=readiness({...ready,entryGate:'BLOCKED'});
  ok('entry gate block blocks readiness',r.state==='BLOCKED');

  r=readiness({...ready,timing:'RISK_ELEVATED'});
  ok('timing risk produces review not block',r.state==='REVIEW');
  ok('timing review is explicit',r.reviews.includes('TIMING_REVIEW'));

  r=readiness({...ready,verification:'PARTIAL'});
  ok('partial verification produces review',r.state==='REVIEW');

  r=readiness({...ready,contradictionCount:2});
  ok('unresolved contradictions produce review',r.state==='REVIEW');

  r=readiness({...ready,unresolvedCriticalCount:1});
  ok('critical unresolved item blocks readiness',r.state==='BLOCKED');

  ok('first unresolved stage is BUSINESS',nextUnresolvedStage({...ready,business:'REVIEW'})==='BUSINESS');
  ok('stock follows business in decision chain',nextUnresolvedStage({...ready,stock:'REVIEW'})==='STOCK');
  ok('timing follows stock in decision chain',nextUnresolvedStage({...ready,timing:'REVIEW'})==='TIMING');
  ok('why-wrong follows timing in decision chain',nextUnresolvedStage({...ready,whyWrong:'REVIEW'})==='WHY_WRONG');
  ok('evidence follows core chain when still unresolved',nextUnresolvedStage({...ready,evidenceGapState:'REVIEW_WITH_GAPS'})==='EVIDENCE');
  ok('entry gate remains final gate',nextUnresolvedStage({...ready,entryGate:'REVIEW'})==='ENTRY_GATE');

  const cp=checkpoint({...ready,stock:'REVIEW'});
  ok('checkpoint names unresolved stage',cp.next==='STOCK');
  ok('checkpoint remains non-trading',cp.tradeSignal===false);

  const b=batch([
    ready,
    {...ready,symbol:'BBB',timing:'REVIEW'},
    {...ready,symbol:'CCC',business:'BLOCKED'}
  ]);
  ok('batch separates ready/review/blocked',b.ready.length===1&&b.review.length===1&&b.blocked.length===1);
  ok('batch remains non-trading',b.tradeSignal===false);

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

const API={
  VERSION,ALLOWED_STAGE_STATES,normalizeInput,blockers,reviews,
  stageCompleteness,readiness,nextUnresolvedStage,checkpoint,batch,selfTest
};

try{API.selfTestResult=selfTest()}catch(e){API.bootstrapError=String(e&&e.stack||e)}
global.ZooInvestDecisionReadinessGateEngine=Object.freeze(API);

})(engineContext);
